// chatSlice.ts
import { createSlice } from "@reduxjs/toolkit"
import {
    AssistantTypes,
    ImproveSearchQueryModes,
    ModelVersions,
    PreselectContextFilesModes,
    SearchApproaches,
    TokensUsageModes,
    UserTypes,
} from "@/api"
import type { VisualEvidence } from "@/api"
import type { SourceContext, SourceBinding } from "@/api/sourceContext"
import type { DiagnosticStep } from "@/api/diagnostics"
import type { FileSchemaResponse } from "@/api/api"
import type { SqlScopeSelection } from "@/api/models"
import { DownloadType } from "@/components/DownloadFromBlob/downloadFromBlob"
import { DEFAULT_CITATIONS_HIGHLIGHT_MODE } from "@/components/CitationPDFViewer/citationsHighlightMode"
import { DEFAULT_USE_REASONING } from "@/utils/brand"

type ContextFileMetadata = {
    id: string
    file_name?: string | null
    is_folder?: boolean
    is_real_time?: boolean
    file_source?: string
}

export type PendingVisualReview = {
    binding?: SourceBinding
    answerKey?: string
    evidenceId: string
    dataUrl: string
    name: string
    label: string
    source: string
    evidence?: VisualEvidence
}

function cacheSelectedContextFileMetadata(
    selectedIds: string[],
    existingMetadata: ContextFileMetadata[],
    knownDocuments: ContextFileMetadata[]
): ContextFileMetadata[] {
    const selected = new Set(selectedIds)
    const retainedById = new Map(existingMetadata.filter(file => selected.has(file.id)).map(file => [file.id, file]))

    for (const file of knownDocuments) {
        if (selected.has(file.id) && file.file_name) {
            retainedById.set(file.id, { ...file })
        }
    }

    return selectedIds.map(id => retainedById.get(id)).filter((file): file is ContextFileMetadata => Boolean(file))
}

type ConversationResetState = {
    answers: string[]
    chatId: string
    liveSteps: DiagnosticStep[]
    pendingUserImages: { dataUrl: string; name: string }[]
    pendingUserFiles: { dataUrl: string; name: string; mimeType: string }[]
    pendingVisualReviews: PendingVisualReview[]
    conversationIsOwner: boolean
    conversationOwnerEmail: string | null
    draftSourceContext: SourceContext | null
    draftSqlScope: SqlScopeSelection | null
}

function resetConversationState(state: ConversationResetState) {
    state.answers = []
    state.chatId = ""
    state.liveSteps = []
    state.pendingUserImages = []
    state.pendingUserFiles = []
    state.pendingVisualReviews = []
    state.conversationIsOwner = true
    state.conversationOwnerEmail = null
    state.draftSourceContext = null
    state.draftSqlScope = null
}

export const chatSlice = createSlice({
    name: "chat",
    initialState: {
        answers: [] as string[],
        answerStream: "",
        reasoningStream: "",
        liveSteps: [] as DiagnosticStep[],
        isStreamStopped: false,
        chatTitle: "",
        chatError: "",
        isLoading: false,
        isLoadingHistory: false,
        answerGenerationPromptTemplate: "",
        cognitiveSearchQueryGenerationPromptTemplate: "",
        excludeCategory: "",
        lastQuestionRef: "",
        activeCitation: "",
        activeAnalysisPanelTab: "",
        temperatureCount: 0.3,
        docsSearchThreshold: 30,
        chatIsPinned: false,
        useSuggestFollowupQuestions: true,
        searchApproach: SearchApproaches.Auto,
        improveSearchQueryMode: ImproveSearchQueryModes.Manual,
        model: ModelVersions.PRO,
        userType: UserTypes.Expert,
        selectedTokensUsageMode: TokensUsageModes.Increased,
        modelProvider: "",
        docs: [],
        allDocuments: [] as FileSchemaResponse[],
        projectContextFiles: [],
        /** File metadata stubs for conversation-restored context files missing from allDocuments. */
        restoredContextFiles: [] as ContextFileMetadata[],
        selectedDocs: [],
        pagination: { pageSize: 10, page: 0 },
        filters: { items: [] },
        documentsVisibleColumns: {
            id: false,
            brokerId: false,
            status: false,
            from_email: false,
            to_email: false,
            sent_date: false,
        },
        doc_sort: [{ field: "updated_time_utc", sort: "desc" }],
        chatId: "",
        docsSmartSearchActive: false,
        conversationsSmartSearchActive: false,
        assistantType: AssistantTypes.DOC_ASSISTANT,
        conversations: [],
        conversationsSortModel: [{ field: "updated_time_utc", sort: "desc" }],
        conversationsFilterModel: { items: [] },
        conversationsPaginationModel: { pageSize: 10, page: 0 },
        conversationsDensity: "standard",
        projectsPaginationModel: { pageSize: 10, page: 0 },
        projectsSortModel: [{ field: "updated_time_utc", sort: "desc" }],
        projectsTotalCount: 0,
        showWelcomeMessage: false,
        subscriptionPlanStatus: {
            planName: undefined,
            status: undefined,
        },
        pdfCitationDisplayMode: DownloadType.Auto,
        citationsHighlightMode: DEFAULT_CITATIONS_HIGHLIGHT_MODE,
        fileFormats: [],
        fileSources: [],
        documentsDateRange: [null, null],
        fileSizeRange: [0, 100],
        aiTags: [],
        manualTags: [],
        documentsSearchProfile: "Everything",
        documentsSearchQuery: "",
        conversationsSearchProfile: "everything",
        conversationsSearchQuery: "",
        conversationsOptimizedQuery: "",
        conversationsDateRange: [null, null],
        conversationsDatePreset: "all",
        conversationsSearchThreshold: 30,
        currentFolderPath: null,
        docsPreselectSearchThreshold: 50,
        preselectContextFilesMode: PreselectContextFilesModes.Enabled,
        latestTranslationLanguage: "en",
        // IFC Viewer mode: "select" or "showOnly"
        ifcViewMode: "select",
        currentProjectId: null as string | null,
        currentProjectName: null as string | null,
        conversationIsOwner: true,
        conversationOwnerEmail: null as string | null,
        showDataSourcesSidebar: false,
        dataSourcesViewMode: "all" as "all" | "selected",
        draftQuestion: "",
        useReasoning: DEFAULT_USE_REASONING,
        allowGeneralKnowledge: true,
        pendingUserImages: [] as { dataUrl: string; name: string }[],
        pendingUserFiles: [] as { dataUrl: string; name: string; mimeType: string }[],
        pendingVisualReviews: [] as PendingVisualReview[],
        /** Explicit page/region/text scope for the next question. Null keeps legacy behavior. */
        draftSourceContext: null as SourceContext | null,
        draftSqlScope: null as SqlScopeSelection | null,
        useAiTitles:
            (typeof window !== "undefined" && window.localStorage?.getItem("aidocr.useAiTitles") === "true") || false,
    },
    reducers: {
        setAnswer: (state, action) => {
            state.answers = action.payload
        },
        addAnswer: (state, action) => {
            state.answers = [...state.answers, action.payload]
        },
        setAnswerStream: (state, action) => {
            state.answerStream = action.payload
        },
        appendToAnswerStream: (state, action) => {
            let next = (state.answerStream || "") + action.payload
            // Drop leading newlines until substantive answer text arrives (avoids blank
            // lines between the ThinkingBlock and the streamed answer in the UI).
            if (!(state.answerStream || "").trim()) {
                next = next.replace(/^\n+/, "")
            }
            state.answerStream = next
        },
        setReasoningStream: (state, action) => {
            state.reasoningStream = action.payload
        },
        appendToReasoningStream: (state, action) => {
            state.reasoningStream = (state.reasoningStream || "") + action.payload
        },
        setLiveSteps: (state, action) => {
            state.liveSteps = action.payload || []
        },
        appendLiveSteps: (state, action) => {
            const incoming = action.payload
            if (!incoming?.length) return
            state.liveSteps = [...(state.liveSteps || []), ...incoming]
        },
        clearLiveSteps: state => {
            state.liveSteps = []
        },
        clearAnswers: state => {
            state.answers = []
            state.chatId = ""
            state.liveSteps = []
            state.pendingUserImages = []
            state.pendingUserFiles = []
            state.pendingVisualReviews = []
            state.restoredContextFiles = []
            state.draftSourceContext = null
            state.draftSqlScope = null
        },
        clearAnswersPreservingContextFiles: state => {
            resetConversationState(state)
        },
        setChatError: (state, action) => {
            state.chatError = action.payload
        },
        setChatLoading: (state, action) => {
            state.isLoading = action.payload
        },
        setHistoryLoading: (state, action) => {
            state.isLoadingHistory = action.payload
        },
        setActiveCitation: (state, action) => {
            state.activeCitation = action.payload
        },
        setActiveAnalysisPanelTab: (state, action) => {
            state.activeAnalysisPanelTab = action.payload
        },
        setAnswerGenerationPromptTemplate: (state, action) => {
            state.answerGenerationPromptTemplate = action.payload
        },
        setCognitiveSearchQueryGenerationPromptTemplate: (state, action) => {
            state.cognitiveSearchQueryGenerationPromptTemplate = action.payload
        },
        setExcludeCategory: (state, action) => {
            state.excludeCategory = action.payload
        },
        setLastQuestionRef: (state, action) => {
            state.lastQuestionRef = action.payload
        },
        setTemperatureCount: (state, action) => {
            state.temperatureCount = action.payload
        },
        setUseSuggestFollowupQuestions: (state, action) => {
            state.useSuggestFollowupQuestions = action.payload
        },
        setPreselectContexFilesMode: (state, action) => {
            state.preselectContextFilesMode = action.payload
        },
        setSearchApproach: (state, action) => {
            state.searchApproach = action.payload
        },
        setImproveSearchQueryMode: (state, action) => {
            state.improveSearchQueryMode = action.payload
        },
        setSelectedDocuments: (state, action) => {
            if (!action.payload || action.payload.length === 0) {
                state.selectedDocs = []
                state.restoredContextFiles = []
            } else {
                const uniqueDocs = Array.from(new Set(action.payload))
                // @ts-expect-error payload is string[]
                state.selectedDocs = uniqueDocs
                state.restoredContextFiles = cacheSelectedContextFileMetadata(
                    uniqueDocs as string[],
                    state.restoredContextFiles,
                    [
                        ...(state.allDocuments || []),
                        ...(state.projectContextFiles || []),
                        ...(state.restoredContextFiles || []),
                    ]
                )
            }
        },
        setRestoredContextFiles: (state, action) => {
            state.restoredContextFiles = action.payload || []
        },
        setDocs: (state, action) => {
            state.docs = action.payload
        },
        setModel: (state, action) => {
            state.model = action.payload
        },
        setUserType: (state, action) => {
            state.userType = action.payload
        },
        setTokensUsageMode: (state, action) => {
            state.selectedTokensUsageMode = action.payload
        },
        setChatId: (state, action) => {
            if (state.chatId !== action.payload) state.draftSqlScope = null
            state.chatId = action.payload
        },
        setPagination: (state, action) => {
            state.pagination = action.payload
        },
        setFilters: (state, action) => {
            state.filters = action.payload
        },
        setSort: (state, action) => {
            state.doc_sort = action.payload
        },
        setDocsSmartSearchActive: (state, action) => {
            state.docsSmartSearchActive = action.payload
        },
        setConversationsSmartSearchActive: (state, action) => {
            state.conversationsSmartSearchActive = action.payload
        },
        setAssistantType: (state, action) => {
            state.assistantType = action.payload
        },
        setConversationsSortModel: (state, action) => {
            state.conversationsSortModel = action.payload
        },
        setConversationsFilterModel: (state, action) => {
            state.conversationsFilterModel = action.payload
        },
        setConversationsPaginationModel: (state, action) => {
            state.conversationsPaginationModel = action.payload
        },
        setProjectsPaginationModel: (state, action) => {
            state.projectsPaginationModel = action.payload
        },
        setProjectsSortModel: (state, action) => {
            state.projectsSortModel = action.payload
        },
        setProjectsTotalCount: (state, action) => {
            state.projectsTotalCount = action.payload
        },
        setConversations: (state, action) => {
            state.conversations = action.payload
        },
        setConversationsDensity: (state, action) => {
            state.conversationsDensity = action.payload
        },
        setShowWelcomeMessage: (state, action) => {
            state.showWelcomeMessage = action.payload
        },
        setSubscriptionPlanStatus: (state, action) => {
            state.subscriptionPlanStatus = action.payload
        },
        setPdfCitationDisplayMode: (state, action) => {
            state.pdfCitationDisplayMode = action.payload
        },
        setCitationsHighlightMode: (state, action) => {
            state.citationsHighlightMode = action.payload
        },
        setChatTitle: (state, action) => {
            state.chatTitle = action.payload
        },
        setChatIsPinned: (state, action) => {
            state.chatIsPinned = action.payload
        },
        setDocsSearchThreshold: (state, action) => {
            state.docsSearchThreshold = action.payload
        },
        setDocumentsVisibleColumns: (state, action) => {
            state.documentsVisibleColumns = action.payload
        },
        setAllDocuments: (state, action) => {
            const next = Array.isArray(action.payload) ? action.payload : []
            const prev = state.allDocuments ?? []
            if (prev.length === next.length) {
                const signature = (docs: Array<{ id?: string; file_name?: string | null; ai_title?: string | null }>) =>
                    docs.map(d => `${d?.id ?? ""}|${d?.file_name ?? ""}|${d?.ai_title ?? ""}`).join("\0")
                if (signature(prev) === signature(next)) {
                    return
                }
            }
            state.allDocuments = next
        },
        setProjectContextFiles: (state, action) => {
            state.projectContextFiles = action.payload
        },
        setModelProvider: (state, action) => {
            state.modelProvider = action.payload
        },
        setFileFormats: (state, action) => {
            state.fileFormats = action.payload
        },
        setFileSources: (state, action) => {
            state.fileSources = action.payload
        },
        setDocumentsDateRange: (state, action) => {
            state.documentsDateRange = action.payload
        },
        setFileSizeRange: (state, action) => {
            state.fileSizeRange = action.payload
        },
        setManualTags: (state, action) => {
            state.manualTags = action.payload
        },
        setDocumentsSearchProfile: (state, action) => {
            state.documentsSearchProfile = action.payload
        },
        setDocumentsSearchQuery: (state, action) => {
            state.documentsSearchQuery = action.payload
        },
        setConversationsSearchProfile: (state, action) => {
            state.conversationsSearchProfile = action.payload
        },
        setConversationsSearchQuery: (state, action) => {
            state.conversationsSearchQuery = action.payload
        },
        setConversationsOptimizedQuery: (state, action) => {
            state.conversationsOptimizedQuery = action.payload
        },
        setConversationsDateRange: (state, action) => {
            state.conversationsDateRange = action.payload
        },
        setConversationsDatePreset: (state, action) => {
            state.conversationsDatePreset = action.payload
        },
        setConversationsSearchThreshold: (state, action) => {
            state.conversationsSearchThreshold = action.payload
        },
        setIsStreamStopped: (state, action) => {
            state.isStreamStopped = action.payload
        },
        setCurrentFolderPath: (state, action) => {
            state.currentFolderPath = action.payload
        },
        setDocsPreselectSearchThreshold: (state, action) => {
            state.docsPreselectSearchThreshold = action.payload
        },
        setPreselectContextFilesMode: (state, action) => {
            state.preselectContextFilesMode = action.payload
        },
        setLatestTranslationLanguage: (state, action) => {
            state.latestTranslationLanguage = action.payload
        },
        setIfcViewMode: (state, action) => {
            state.ifcViewMode = action.payload
        },
        setCurrentProjectId: (state, action) => {
            state.currentProjectId = action.payload
        },
        setCurrentProjectName: (state, action) => {
            state.currentProjectName = action.payload
        },
        setConversationAuthorship: (state, action) => {
            state.conversationIsOwner = action.payload?.isOwner ?? true
            state.conversationOwnerEmail = action.payload?.ownerEmail ?? null
        },
        setShowDataSourcesSidebar: (state, action) => {
            if (typeof action.payload === "boolean") {
                state.showDataSourcesSidebar = action.payload
                return
            }
            state.showDataSourcesSidebar = action.payload.show
            if (action.payload.viewMode) {
                state.dataSourcesViewMode = action.payload.viewMode
            }
        },
        setDraftQuestion: (state, action) => {
            state.draftQuestion = action.payload
        },
        setDraftSqlScope: (state, action) => {
            state.draftSqlScope = action.payload || null
        },
        setDraftSourceContext: (state, action) => {
            state.draftSourceContext = action.payload || null
        },
        clearDraftSourceContext: state => {
            state.draftSourceContext = null
            state.draftSqlScope = null
        },
        setUseReasoning: (state, action) => {
            state.useReasoning = action.payload
        },
        setAllowGeneralKnowledge: (state, action) => {
            state.allowGeneralKnowledge = action.payload
        },
        setUseAiTitles: (state, action) => {
            state.useAiTitles = action.payload
            window.localStorage?.setItem("aidocr.useAiTitles", action.payload ? "true" : "false")
        },
        setPendingUserImages: (state, action) => {
            state.pendingUserImages = action.payload
        },
        clearPendingUserImages: state => {
            state.pendingUserImages = []
        },
        setPendingUserFiles: (state, action) => {
            state.pendingUserFiles = action.payload
        },
        clearPendingUserFiles: state => {
            state.pendingUserFiles = []
        },
        addPendingVisualReview: (state, action) => {
            const item = action.payload as PendingVisualReview
            if (item.binding) {
                const selectedSource = item.binding.source
                if (state.draftSourceContext) {
                    state.draftSourceContext.bindings = state.draftSourceContext.bindings.filter(
                        binding =>
                            !(
                                binding.mode === "whole" &&
                                binding.source.kind === selectedSource.kind &&
                                binding.source.source_id === selectedSource.source_id &&
                                binding.source.asset_id === selectedSource.asset_id &&
                                binding.source.revision === selectedSource.revision
                            )
                    )
                }
                if (
                    selectedSource.kind === "knowledge_file" &&
                    !state.draftSourceContext?.bindings.some(
                        binding =>
                            binding.source.kind === "knowledge_file" &&
                            binding.source.source_id === selectedSource.source_id
                    )
                ) {
                    state.selectedDocs = state.selectedDocs.filter(id => id !== selectedSource.source_id)
                }
            }
            const existingIndex = state.pendingVisualReviews.findIndex(review => review.evidenceId === item.evidenceId)
            if (existingIndex >= 0) state.pendingVisualReviews[existingIndex] = item
            else state.pendingVisualReviews.push(item)
        },
        removePendingVisualReview: (state, action) => {
            state.pendingVisualReviews = state.pendingVisualReviews.filter(
                review => review.evidenceId !== action.payload
            )
        },
        clearPendingVisualReviews: state => {
            state.pendingVisualReviews = []
        },
    },
})

export const {
    setHistoryLoading,
    setAnswer,
    setAnswerStream,
    appendToAnswerStream,
    setReasoningStream,
    appendToReasoningStream,
    setLiveSteps,
    appendLiveSteps,
    clearLiveSteps,
    clearAnswers,
    clearAnswersPreservingContextFiles,
    setChatLoading,
    setChatError,
    setActiveCitation,
    setActiveAnalysisPanelTab,
    setAnswerGenerationPromptTemplate,
    setCognitiveSearchQueryGenerationPromptTemplate,
    setExcludeCategory,
    setLastQuestionRef,
    setTemperatureCount,
    setUseSuggestFollowupQuestions,
    setPreselectContexFilesMode,
    setSearchApproach,
    setImproveSearchQueryMode,
    setSelectedDocuments,
    setDraftSourceContext,
    clearDraftSourceContext,
    setModel,
    setUserType,
    setTokensUsageMode,
    setPagination,
    setFilters,
    setChatId,
    setSort,
    setDocs,
    setAssistantType,
    setConversationsSortModel,
    setConversationsFilterModel,
    setConversationsPaginationModel,
    setProjectsPaginationModel,
    setProjectsSortModel,
    setProjectsTotalCount,
    setConversations,
    setConversationsDensity,
    setShowWelcomeMessage,
    setSubscriptionPlanStatus,
    setPdfCitationDisplayMode,
    setCitationsHighlightMode,
    setChatTitle,
    setChatIsPinned,
    setDocsSearchThreshold,
    setDocsPreselectSearchThreshold,
    setPreselectContextFilesMode,
    setDocumentsVisibleColumns,
    setAllDocuments,
    setProjectContextFiles,
    setRestoredContextFiles,
    setModelProvider,
    setFileFormats,
    setFileSources,
    setDocumentsDateRange,
    setFileSizeRange,
    setManualTags,
    setDocumentsSearchProfile,
    setDocumentsSearchQuery,
    setConversationsSearchProfile,
    setConversationsSearchQuery,
    setConversationsOptimizedQuery,
    setConversationsDateRange,
    setConversationsDatePreset,
    setConversationsSearchThreshold,
    setDocsSmartSearchActive,
    setConversationsSmartSearchActive,
    setIsStreamStopped,
    setCurrentFolderPath,
    setLatestTranslationLanguage,
    setIfcViewMode,
    setCurrentProjectId,
    setCurrentProjectName,
    setConversationAuthorship,
    setShowDataSourcesSidebar,
    setDraftQuestion,
    setDraftSqlScope,
    setUseReasoning,
    setAllowGeneralKnowledge,
    setUseAiTitles,
    setPendingUserImages,
    clearPendingUserImages,
    setPendingUserFiles,
    clearPendingUserFiles,
    addPendingVisualReview,
    removePendingVisualReview,
    clearPendingVisualReviews,
} = chatSlice.actions

export default chatSlice.reducer
